import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import Modal from 'components/atoms/Modal'
import { TextField, Checkbox } from '@mui/material'
import { getUserBps } from 'features/bpList/ducks/selectors'
import { fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import SearchIcon from '@mui/icons-material/Search'

const CreatePortfolioModal = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  initialSelectedBpIds = [],
  pinnedBps = [],
  intro = ''
}) => {
  const dispatch = useDispatch()
  const allBps = useSelector(getUserBps)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedBpIds, setSelectedBpIds] = useState([])
  const [bpSearch, setBpSearch] = useState('')
  const isEdit = !!initialValues
  const initialSelectedKey = initialSelectedBpIds.join('|')

  useEffect(() => {
    if (!open) return
    setName(initialValues?.name || '')
    setDescription(initialValues?.description || '')
    setSelectedBpIds(initialSelectedBpIds)
    setBpSearch('')
    if (!isEdit && allBps.length === 0) {
      dispatch(fetchUserBpsRoutine({ page: 1, sort: '-created_at' }))
    }
  }, [open, initialValues, initialSelectedKey])

  const availableBps = useMemo(
    () =>
      allBps
        .filter(bp => bp.status === 'active')
        .filter(
          bp =>
            !pinnedBps.some(pinnedBp => String(pinnedBp.id) === String(bp.id))
        )
        .filter(
          bp =>
            !bpSearch || bp.name?.toLowerCase().includes(bpSearch.toLowerCase())
        )
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allBps, bpSearch, pinnedBps]
  )

  const toggleBp = id => {
    setSelectedBpIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const handleSubmit = () => {
    if (!name.trim()) return
    const payload = {
      name: name.trim(),
      description: description.trim() || null
    }
    if (!isEdit && selectedBpIds.length > 0) {
      payload.bpIds = selectedBpIds
    }
    onSubmit(payload)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Portfolio' : 'Create Portfolio'}
      maxWidth='sm'
      fullWidth
    >
      <Form>
        {intro && <IntroText>{intro}</IntroText>}
        <TextField
          label='Portfolio Name'
          data-tour-id='portfolios.create-name'
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          autoFocus
          required
          placeholder='e.g. London Properties'
          size='small'
        />
        <TextField
          label='Description (optional)'
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder='What is this portfolio for?'
          size='small'
        />
        {!isEdit && (
          <>
            <BpSectionLabel>
              Add Building Passports{' '}
              {selectedBpIds.length > 0 && `(${selectedBpIds.length} selected)`}
            </BpSectionLabel>
            {pinnedBps.length > 0 && (
              <PinnedList>
                {pinnedBps.map(bp => (
                  <PinnedBpRow key={bp.id}>
                    <Checkbox checked disabled size='small' />
                    <BpInfo>
                      <BpName>{bp.name}</BpName>
                      {(bp.street || bp.city) && (
                        <BpAddr>
                          {[bp.street, bp.city].filter(Boolean).join(', ')}
                        </BpAddr>
                      )}
                    </BpInfo>
                    <PinnedBadge>Selected</PinnedBadge>
                  </PinnedBpRow>
                ))}
              </PinnedList>
            )}
            <SearchBar>
              <SearchIcon style={{ fontSize: 16, color: '#999' }} />
              <SearchInput
                placeholder='Search Building Passports...'
                value={bpSearch}
                onChange={e => setBpSearch(e.target.value)}
              />
            </SearchBar>
            <BpList>
              {availableBps.length === 0 ? (
                <BpEmpty>
                  {bpSearch
                    ? 'No matching Building Passports'
                    : 'No Building Passports available'}
                </BpEmpty>
              ) : (
                availableBps.slice(0, 50).map(bp => (
                  <BpRow key={bp.id} onClick={() => toggleBp(bp.id)}>
                    <Checkbox
                      checked={selectedBpIds.includes(bp.id)}
                      size='small'
                    />
                    <BpInfo>
                      <BpName>{bp.name}</BpName>
                      {bp.street && (
                        <BpAddr>
                          {bp.street}, {bp.city}
                        </BpAddr>
                      )}
                    </BpInfo>
                  </BpRow>
                ))
              )}
            </BpList>
          </>
        )}
        <Actions>
          <CancelBtn onClick={onClose}>Cancel</CancelBtn>
          <SubmitBtn data-tour-id='portfolios.create-submit' onClick={handleSubmit} disabled={!name.trim()}>
            {isEdit
              ? 'Save'
              : selectedBpIds.length > 0
                ? `Create with ${selectedBpIds.length} Building Passport${selectedBpIds.length !== 1 ? 's' : ''}`
                : 'Create'}
          </SubmitBtn>
        </Actions>
      </Form>
    </Modal>
  )
}

export default CreatePortfolioModal

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px 0;
`

const IntroText = styled.div`
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 6px;
  background: rgba(51, 190, 204, 0.08);
  color: rgba(12, 18, 41, 0.72);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  padding: 10px 12px;
`

const BpSectionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-top: 4px;
`

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  background: transparent;
  &::placeholder {
    color: #ccc;
  }
`

const BpList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
`

const PinnedList = styled.div`
  border: 1px solid rgba(51, 190, 204, 0.28);
  border-radius: 6px;
  background: rgba(51, 190, 204, 0.06);
  overflow: hidden;
`

const PinnedBpRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
`

const BpRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  cursor: pointer;
  &:not(:last-child) {
    border-bottom: 1px solid #f2f2f2;
  }
  &:hover {
    background: #fafafa;
  }
`

const BpInfo = styled.div`
  min-width: 0;
`
const BpName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`
const BpAddr = styled.div`
  font-size: 11px;
  color: #999;
`
const BpEmpty = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: #999;
`

const PinnedBadge = styled.span`
  border-radius: 999px;
  background: rgba(51, 190, 204, 0.14);
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 7px 9px;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
`

const CancelBtn = styled.button`
  padding: 8px 16px;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  &:hover {
    background: #f9f9f9;
  }
`

const SubmitBtn = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
